import React from 'react';

class OwnerDiagnosticResults extends React.Component {
  zoneLabel() {
    const { quadrant } = this.props;
    switch (quadrant) {
      case 'best_zone':
        return { label: 'Best Zone', bg: 'bg-succession-success', text: 'text-white' };
      case 'fix_owner':
        return { label: 'Fix the Owner', bg: 'bg-succession-warning', text: 'text-black' };
      case 'fix_business':
        return { label: 'Fix the Business', bg: 'bg-succession-warning', text: 'text-black' };
      default:
        return { label: 'Danger Zone', bg: 'bg-succession-error', text: 'text-white' };
    }
  }

  render() {
    const { readinessTotal, attractivenessTotal, quadrant } = this.props;
    const zone = this.zoneLabel();

    const isActive = (key) => (
      (key === 'danger_zone' && quadrant === 'danger_zone') ||
      (key === 'fix_business' && quadrant === 'fix_business') ||
      (key === 'fix_owner' && quadrant === 'fix_owner') ||
      (key === 'best_zone' && quadrant === 'best_zone')
    );

    const highlightClass = (key) => {
      const base = 'rounded-md border flex flex-col items-center justify-center gap-2 text-center p-4 text-sm md:text-base';
      if (isActive(key)) {
        // Fill entire quadrant with primary color and make text white
        return base + ' border-transparent bg-succession-primary text-white';
      }
      return base + ' border-succession-border bg-white';
    };

    // Normalize scores to 0..1 to place a dot
    const norm = (v) => {
      const x = ((v || 6) - 6) / 24; // 6..30 -> 0..1
      return Math.max(0, Math.min(1, x));
    };
    const xPct = Math.round(norm(readinessTotal) * 100);
    const yPct = Math.round(norm(attractivenessTotal) * 100); // 0 low .. 100 high

    return (
      <div className="min-h-screen flex justify-center bg-succession-surface px-4 pt-4 pb-10">
        <div className="w-full max-w-5xl bg-white rounded-xl shadow p-6 md:p-10">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl md:text-3xl font-semibold text-succession-foreground">Business Attractiveness vs. Owner Readiness</h1>
            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${zone.bg} ${zone.text}`}>{zone.label}</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="p-4 rounded-lg border border-succession-border">
              <div className="text-succession-muted mb-2">Owner Readiness (Total)</div>
              <div className="text-3xl font-bold text-succession-foreground">{readinessTotal || 0}</div>
            </div>
            <div className="p-4 rounded-lg border border-succession-border">
              <div className="text-succession-muted mb-2">Business Attractiveness (Total)</div>
              <div className="text-3xl font-bold text-succession-foreground">{attractivenessTotal || 0}</div>
            </div>
          </div>

          <div className="relative border border-succession-border rounded-lg p-6">
            <div className="mb-4 font-semibold text-succession-foreground">Business Attractiveness vs. Owner Readiness</div>
            {/* Flex layout to avoid grid template quirks on some browsers */}
            <div className="flex gap-6 items-stretch">
              {/* Y-axis labels */}
              <div className="w-48 md:w-56 pr-2 py-2 text-sm md:text-base font-semibold text-succession-foreground">
                <div className="h-1/2 flex items-center justify-end pr-1">
                  <span className="text-right leading-tight">Attractive (High Business Attractiveness)</span>
                </div>
                <div className="h-1/2 flex items-center justify-end pr-1">
                  <span className="text-right leading-tight">Not Attractive (Low Business Attractiveness)</span>
                </div>
              </div>
              {/* X-axis + Matrix */}
              <div className="flex-1 flex flex-col">
                {/* Top X-axis labels */}
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div className="text-center text-sm md:text-base font-semibold text-succession-foreground">Not Ready (Low Readiness)</div>
                  <div className="text-center text-sm md:text-base font-semibold text-succession-foreground">Ready (High Readiness)</div>
                </div>
                <div className="relative w-full h-96 md:h-[520px] bg-white rounded-md overflow-hidden">
                  {/* central axis lines */}
                  <div className="absolute left-1/2 top-4 bottom-4 w-px md:w-[2px] bg-succession-border/70" />
                  <div className="absolute top-1/2 left-4 right-4 h-px md:h-[2px] bg-succession-border/70" />

                  {/* quadrant grid */}
                  <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 gap-6 p-6 pointer-events-none">
                    {/* Top row (Attractive) */}
                    <div className={highlightClass('fix_owner')}>
                      <div className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-semibold ${isActive('fix_owner') ? 'bg-succession-warning text-black' : 'text-black bg-succession-warning'}`}>Fix the Owner</div>
                      <p className={`${isActive('fix_owner') ? 'text-white' : 'text-succession-foreground'} text-sm leading-relaxed`}>business strong, but owner not prepared</p>
                    </div>
                    <div className={highlightClass('best_zone')}>
                      <div className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-semibold ${isActive('best_zone') ? 'bg-succession-success text-white' : 'text-white bg-succession-success'}`}>Best Zone</div>
                      <p className={`${isActive('best_zone') ? 'text-white' : 'text-succession-foreground'} text-sm leading-relaxed`}>both ready and attractive; optimal for transition</p>
                    </div>
                    {/* Bottom row (Not Attractive) */}
                    <div className={highlightClass('danger_zone')}>
                      <div className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-semibold ${isActive('danger_zone') ? 'bg-succession-error text-white' : 'text-white bg-succession-error'}`}>Danger Zone</div>
                      <p className={`${isActive('danger_zone') ? 'text-white' : 'text-succession-foreground'} text-sm leading-relaxed`}>work on personal + business improvement</p>
                    </div>
                    <div className={highlightClass('fix_business')}>
                      <div className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-semibold ${isActive('fix_business') ? 'bg-succession-warning text-black' : 'text-black bg-succession-warning'}`}>Fix the Business</div>
                      <p className={`${isActive('fix_business') ? 'text-white' : 'text-succession-foreground'} text-sm leading-relaxed`}>owner ready, but business not marketable</p>
                    </div>
                  </div>

                  {/* user point */}
                  <div
                    className="absolute w-4 h-4 rounded-full bg-succession-primary shadow"
                    style={{ left: `calc(${xPct}% - 8px)`, top: `calc(${100 - yPct}% - 8px)` }}
                    title="Your position"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Intentionally no automatic transition to Wealth Gap; results page stands alone */}
        </div>
      </div>
    );
  }
}

export default OwnerDiagnosticResults;


